import { useHasPermissions } from "@/components/misc";
import { Collapsible, NavigationMenu } from "@/components/ui";
import {
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils/cn";
import { Link, useMatchRoute } from "@tanstack/react-router";
import { ChevronRightIcon } from "lucide-react";
import * as React from "react";
import type { CustomLinkProps, SubLinkProps } from "../app-sidebar";

export const CollapsibleSidebarLink: React.FC<CustomLinkProps> = ({
  icon: Icon,
  label,
  badge,
  subLinks,
  ...permissions
}) => {
  const { state } = useSidebar();
  const isCollapsed = state !== "expanded";
  const matchRoute = useMatchRoute();
  const hasPermission = useHasPermissions(permissions);
  const [isOpen, setIsOpen] = React.useState(true);
  const [manuallyClosed, setManuallyClosed] = React.useState(false);

  const hasActiveSubLink = React.useMemo(() => {
    if (subLinks == null) return false;
    return subLinks.some((subLink) =>
      Boolean(
        matchRoute({
          to: subLink.to,
          fuzzy: subLink.to !== "/",
        }),
      )
    );
  }, [subLinks, matchRoute]);

  React.useEffect(() => {
    if (!hasActiveSubLink) setManuallyClosed(false);
  }, [hasActiveSubLink]);

  const open = isOpen || (hasActiveSubLink && !manuallyClosed);

  if (!hasPermission && permissions.requires !== undefined) return null;
  if (subLinks == null) return null;

  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <div className="flex w-full">
          <NavigationMenu className="w-auto">
            <NavigationMenu.List>
              <NavigationMenu.Item>
                <NavigationMenu.Trigger
                  className="flex size-8 items-center justify-center rounded-md p-0 outline-hidden transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                  aria-label={label}
                  hideIcon
                >
                  <Icon className="size-5" />
                </NavigationMenu.Trigger>
                <NavigationMenu.Content className="min-w-40">
                  <div className="flex flex-col gap-0.5">
                    {subLinks.map((subLink) => (
                      <NavigationMenu.Link
                        key={subLink.to}
                        render={
                          <Link
                            to={subLink.to}
                            className="flex flex-row items-center gap-2 rounded-sm px-2 py-1 text-sm"
                          />
                        }
                      >
                        <subLink.icon className={cn("size-4", subLink.iconColor)} />
                        <span>{subLink.label}</span>
                      </NavigationMenu.Link>
                    ))}
                  </div>
                </NavigationMenu.Content>
              </NavigationMenu.Item>
            </NavigationMenu.List>
            <NavigationMenu.Portal>
              <NavigationMenu.Positioner side="right" align="start" sideOffset={8}>
                <NavigationMenu.Popup>
                  <NavigationMenu.Viewport />
                </NavigationMenu.Popup>
              </NavigationMenu.Positioner>
            </NavigationMenu.Portal>
          </NavigationMenu>
        </div>
        {badge !== undefined && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
      </SidebarMenuItem>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);
        if (!nextOpen && hasActiveSubLink) setManuallyClosed(true);
        if (nextOpen) setManuallyClosed(false);
      }}
    >
      <SidebarMenuItem>
        <Collapsible.Trigger
          render={
            <SidebarMenuButton tooltip={label} className="min-w-0">
              <Icon className="size-5" />
              <span className="group-data-[collapsible=icon]:hidden min-w-0 flex-1 truncate whitespace-nowrap overflow-hidden">
                {label}
              </span>
              <ChevronRightIcon
                className={cn(
                  "ml-auto size-4 transition-transform duration-200 group-data-[collapsible=icon]:hidden",
                  open && "rotate-90",
                )}
              />
            </SidebarMenuButton>
          }
        />
        {badge !== undefined && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
        <Collapsible.Content className="mt-2">
          <SidebarMenuSub>
            {subLinks.map((subLink) => <SubLink key={subLink.to} {...subLink} />)}
          </SidebarMenuSub>
        </Collapsible.Content>
      </SidebarMenuItem>
    </Collapsible>
  );
};

const SubLink: React.FC<SubLinkProps> = ({ to, icon: Icon, label, iconColor, ...permissions }) => {
  const matchRoute = useMatchRoute();
  const hasPermission = useHasPermissions(permissions);

  if (!hasPermission && permissions.requires !== undefined) return null;

  const isActive = Boolean(
    matchRoute({
      to,
      fuzzy: to !== "/",
    }),
  );

  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton isActive={isActive} render={<Link to={to} />}>
        <Icon className={cn("size-5", iconColor)} />
        <span>{label}</span>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );
};
